import { sameCode } from "features/DataImport/components/DatamapEditor/utils/datamapEdits"
import { DatamapCheckReport, DatamapDoc, DatamapRow, UnknownCodesFinding } from "types/datamap.types"

/** Codes once each, whole numbers written plainly ("007" is "7"), in numeric order. */
const knownCodes = (codes: string[]): string[] =>
  [...new Set(codes.map((code) => (/^-?\d+$/.test(code) ? String(Number(code)) : code)))].sort(
    (a, b) => Number(a) - Number(b) || a.localeCompare(b)
  )

/**
 * The check's findings that the edits so far have not dealt with, plus the unknown codes of data
 * columns linked since. The check runs on the server against a snapshot; this keeps the list
 * current as the user fixes things, without a re-check. Findings follow their rows by variable
 * and show each row's current label.
 */
export const pendingFindings = (report: DatamapCheckReport, doc: DatamapDoc): DatamapCheckReport => {
  const variables = new Set(doc.rows.map((row) => row.variable))
  const catRowOf = (variable: string): DatamapRow | undefined =>
    doc.rows.find((row) => row.type === "CAT" && row.variable === variable)

  const hasCode = (row: DatamapRow, code: string): boolean =>
    row.values.some((value) => value.label !== "" && sameCode(value.code, code))

  const unknownCodes: UnknownCodesFinding[] = report.unknownCodes.flatMap((finding) => {
    const row = catRowOf(finding.variable)
    if (!row) return []
    const codes = finding.codes.filter((code) => !hasCode(row, code.code))
    if (codes.length === 0) return []
    // A code the row lists since the check is one the data uses: the panel must not offer its answer again.
    const listed = finding.codes.filter((code) => hasCode(row, code.code)).map((code) => code.code)
    return [{ ...finding, label: row.label, codes, known: knownCodes([...finding.known, ...listed]) }]
  })

  // A column linked to a category row since the check: its codes were never checked against that row.
  // Like the server, every whole number counts, negatives too ("-99" can never be a listed code).
  for (const column of report.unmappedColumns) {
    const row = catRowOf(column.column)
    if (!row || unknownCodes.some((finding) => finding.variable === row.variable)) continue
    const wholeNumbers = column.values.filter((value) => /^-?\d+$/.test(value.value))
    const unknown = wholeNumbers.filter((value) => !hasCode(row, value.value))
    if (unknown.length === 0) continue
    unknownCodes.push({
      variable: row.variable,
      label: row.label,
      codes: unknown.map((value) => ({ code: value.value, count: value.count })).sort((a, b) => Number(a.code) - Number(b.code)),
      known: knownCodes(wholeNumbers.filter((value) => hasCode(row, value.value)).map((value) => value.value)),
      more: column.more,
    })
  }

  return {
    ...report,
    unmappedColumns: report.unmappedColumns.filter((finding) => !variables.has(finding.column)),
    missingColumns: report.missingColumns.filter((finding) => variables.has(finding.variable)),
    unknownCodes,
    badCells: report.badCells.flatMap((finding) => {
      const row = doc.rows.find((r) => r.variable === finding.variable && r.type === finding.type)
      return row ? [{ ...finding, label: row.label }] : []
    }),
  }
}
